import { Loader, Modal } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconBrandGithub, IconCheck, IconExclamationCircle, IconUnlink } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Endpoints } from '@xver/apis';
import type {
    AlreadyAuthorizedResponse,
    AuthRedirectResponse,
    AuthorizationResponse,
} from '@xver/apis/v1/kufer/github/model';
import { getStatusCode } from '@xver/utils/error';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../../../components/Atoms';
import DialogBox from '../../../../../components/Atoms/DialogBox/DialogBox';
import {
    GITHUB_AUTHORIZATION_QUERY_KEY,
    useGitHubAuthQuery,
} from '../../../../../hooks/GitHubHooks/useGitHubAuthQuery';

export default function GitHubTabPanel() {
    const { t } = useTranslation('containerEngine');
    const queryClient = useQueryClient();

    const { data: auth, isLoading, isError, error, refetch } = useGitHubAuthQuery();

    const isConnected = !!auth && !isError;
    const notConnected = isError && getStatusCode(error) === 404;

    const connectMutation = useMutation({
        mutationFn: Endpoints.v1.Kufer.GitHub.getAuthorizeUrl,
        onSuccess: (data) => {
            // eslint-disable-next-line no-console
            console.log('[github connect] response:', data);

            const raw = data as unknown;
            const payload = (
                raw && typeof raw === 'object' && 'data' in (raw as Record<string, unknown>)
                    ? (raw as { data: unknown }).data
                    : raw
            ) as Partial<AlreadyAuthorizedResponse & AuthRedirectResponse>;

            if (payload?.already_authorized) {
                void refetch();
                return;
            }

            const redirect = payload?.redirect_url;
            if (redirect) {
                window.location.href = redirect;
                return;
            }

            // eslint-disable-next-line no-console
            console.error('[github connect] unexpected response shape:', data);
            notifications.show({
                title: t(
                    'github.toast.redirectMissingTitle',
                    'GitHub connect returned no redirect URL'
                ),
                message: t(
                    'github.toast.redirectMissingMessage',
                    'Open DevTools → Network and inspect the response of GET /v1/github/authorize.'
                ),
                icon: <IconExclamationCircle size={18} color="#eb5757" />,
            });
        },
        onError: (err: { message?: string; status?: number }) => {
            const detail = [err?.status ? `HTTP ${err.status}` : null, err?.message]
                .filter(Boolean)
                .join(' — ');
            notifications.show({
                title: t('github.toast.connectErrorTitle', 'GitHub connect failed'),
                message:
                    detail ||
                    t(
                        'github.toast.connectErrorMessage',
                        'Unable to start the GitHub authorization flow.'
                    ),
                icon: <IconExclamationCircle size={18} color="#eb5757" />,
            });
        },
    });

    const disconnectMutation = useMutation({
        mutationFn: () =>
            Endpoints.v1.Kufer.GitHub.deleteAuthorization((auth as AuthorizationResponse).id),
        onSuccess: async () => {
            notifications.show({
                title: t('github.toast.disconnectTitle', 'GitHub disconnected'),
                message: t(
                    'github.toast.disconnectMessage',
                    'Your GitHub account is no longer linked.'
                ),
                icon: <IconCheck size={18} color="#22c55e" />,
            });
            await queryClient.invalidateQueries({
                queryKey: GITHUB_AUTHORIZATION_QUERY_KEY,
            });
        },
        onError: (err: { message?: string }) => {
            notifications.show({
                title: t('github.toast.disconnectErrorTitle', 'Disconnect failed'),
                message:
                    err?.message ??
                    t('github.toast.disconnectErrorMessage', 'Please try again later.'),
                icon: <IconExclamationCircle size={18} color="#eb5757" />,
            });
        },
    });

    const [confirmOpened, { open: openConfirm, close: closeConfirm }] = useDisclosure(false);

    const handleDisconnect = () => {
        if (!isConnected) return;
        openConfirm();
    };

    const handleConfirmDisconnect = () => {
        disconnectMutation.mutate(undefined, {
            onSuccess: () => closeConfirm(),
        });
    };

    return (
        <>
            <DialogBox
                label={t('github.title', 'GitHub integration')}
                description={t(
                    'github.description',
                    'Link your GitHub account to deploy containers directly from a repository.'
                )}
            >
                {isLoading ? (
                    <div className="w-full flex justify-center py-8">
                        <Loader size={20} color="var(--fg)" />
                    </div>
                ) : isConnected ? (
                    <div className="w-full p-4 flex flex-row items-center gap-4">
                        {auth?.github_avatar_url ? (
                            <img
                                src={auth.github_avatar_url}
                                alt={auth.github_username}
                                className="w-12 h-12 rounded-full"
                            />
                        ) : (
                            <div className="w-12 h-12 rounded-full bg-surface-3 flex items-center justify-center">
                                <IconBrandGithub size={22} />
                            </div>
                        )}
                        <div className="flex flex-col flex-1 min-w-0">
                            <span className="text-base font-semibold text-fg truncate">
                                {auth?.github_username}
                            </span>
                            <div className="inline-flex flex-wrap items-center gap-1 text-sm text-fg/68">
                                <span>
                                    {t('github.connectedAt', 'Connected')}{' '}
                                    {auth?.connected_at
                                        ? dayjs(auth.connected_at).format('DD MMM YYYY, HH:mm')
                                        : '-'}
                                </span>
                            </div>
                        </div>
                        <Button
                            variant="danger"
                            leftIcon={<IconUnlink size={16} stroke={1.5} />}
                            onClick={handleDisconnect}
                            isLoading={disconnectMutation.isPending}
                        >
                            {t('github.disconnectButton', 'Disconnect')}
                        </Button>
                    </div>
                ) : notConnected || !isConnected ? (
                    <div className="w-full p-6 flex flex-col items-start gap-2">
                        <div className="inline-flex items-center gap-2 text-fg">
                            <IconBrandGithub size={22} />
                            <span className="text-base font-semibold">
                                {t('github.notConnected', 'GitHub is not connected')}
                            </span>
                        </div>
                        <p className="text-sm text-fg/68 max-w-2xl">
                            {t(
                                'github.notConnectedHint',
                                'Authorize the xver-cloud GitHub App to read your repositories and pull Dockerfiles. You can revoke access at any time.'
                            )}
                        </p>
                        <Button
                            leftIcon={<IconBrandGithub size={16} />}
                            onClick={() => connectMutation.mutate()}
                            isLoading={connectMutation.isPending}
                            className="mt-1"
                        >
                            {t('github.connectButton', 'Connect GitHub')}
                        </Button>
                    </div>
                ) : null}
            </DialogBox>

            <Modal
                opened={confirmOpened}
                onClose={() => {
                    if (disconnectMutation.isPending) return;
                    closeConfirm();
                }}
                centered
                size="sm"
                closeOnClickOutside={!disconnectMutation.isPending}
                closeOnEscape={!disconnectMutation.isPending}
                title={
                    <div className="inline-flex items-center gap-2">
                        <IconUnlink size={20} color="#eb5757" />
                        <span className="font-semibold">
                            {t('github.disconnectConfirmTitle', 'Disconnect GitHub?')}
                        </span>
                    </div>
                }
            >
                <div className="w-full flex flex-col gap-4">
                    <p className="text-sm text-fg/78">
                        {t(
                            'github.disconnectConfirmBody',
                            'Disconnect your GitHub account? GitHub-source deployments will stop working until you reconnect.'
                        )}
                    </p>
                    <div className="w-full flex flex-row justify-end items-center gap-2">
                        <Button
                            variant="secondary"
                            onClick={closeConfirm}
                            disabled={disconnectMutation.isPending}
                        >
                            {t('github.cancel', 'Cancel')}
                        </Button>
                        <Button
                            variant="danger"
                            onClick={handleConfirmDisconnect}
                            isLoading={disconnectMutation.isPending}
                        >
                            {t('github.disconnectConfirmAction', 'Disconnect')}
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    );
}
